import Axios, {
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import { authService } from './authService'
import store from '../store'
import keycloak, { isSsoSession } from '../keycloak'

// Single source of truth for "this 401 means the TOKEN is dead" across every
// axios stack (this file + api/apis.js). The backend reports the
// machine-readable reason in `code` (legacy HS256 path -> 'token_expired';
// Keycloak path via express-jwt -> 'invalid_token'/'credentials_required'/
// 'token_expired'). Older shapes only had `error`/`message` — and the
// human-readable strings ('Token expired', 'jwt expired') match NONE of the
// codes, which is exactly how expired tokens previously slipped past the
// checks and left the app on an infinite spinner. Keep the message fallbacks.
const TOKEN_ERROR_CODES = [
  'token_expired',
  'invalid_token',
  'credentials_required',
]

export const isTokenInvalidResponse = (errorData: any): boolean => {
  if (
    TOKEN_ERROR_CODES.includes(errorData?.code) ||
    TOKEN_ERROR_CODES.includes(errorData?.error)
  ) {
    return true
  }
  // Some 401 shapes carry only human-readable text: authMiddleware returns
  // {error: 'Token missing email claim'} (Keycloak client misconfig) and
  // {error: 'User not found'} (token for a deleted user) with no code at
  // all, and older paths capitalize ('Token expired', 'Unauthorized').
  // Case-insensitive substring test over BOTH text fields; String() guards
  // against non-string payloads.
  const text = `${String(errorData?.message ?? '')} ${String(
    errorData?.error ?? ''
  )}`.toLowerCase()
  return (
    text.includes('token') ||
    text.includes('expired') ||
    text.includes('unauthorized') ||
    text.includes('user not found')
  )
}

export function getAxiosInstance() {
  const authToken = localStorage.getItem('token')

  const axios = Axios.create({
    baseURL: '/api/platform',
    timeout: 30000,
    headers: { Authorization: `Bearer ${authToken}` },
  })

  axios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Keep SSO tokens fresh at request time. The provider's onTokenExpired
      // timer does not fire in throttled background tabs or across laptop
      // sleep, so the first request after wake would otherwise carry an
      // expired token. A successful refresh re-fires onTokens, which updates
      // localStorage before we read it below.
      if (isSsoSession() && keycloak.authenticated) {
        try {
          await keycloak.updateToken(30)
        } catch {
          // Refresh failed — onAuthRefreshError tears the session down;
          // let the request proceed and fail through normal 401 handling.
        }
      }

      // Get token fresh from localStorage on EVERY request
      let token = localStorage.getItem('token')
      // Self-heal: an earlier bug could persist the literal string "undefined"
      // (or "null") as the token, producing "Authorization: Bearer undefined".
      if (token === 'undefined' || token === 'null') token = null

      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        // Remove header if no token (don't send "Bearer null")
        delete config.headers.Authorization
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Catch 401 errors but only log the user out when the TOKEN itself is invalid,
      // not when a specific resource is unauthorized for an otherwise-valid session.
      if (error.response?.status === 401) {
        // For SSO sessions, first try one forced refresh + retry: a 401 here
        // usually means the request raced token expiry. Only ever retry with
        // a Keycloak token when the session token CAME from Keycloak —
        // retrying a legacy session with keycloak.token could silently
        // switch to a different account held by a stray SSO cookie.
        const retriableConfig = error.config as
          | (InternalAxiosRequestConfig & { kcRetried?: boolean })
          | undefined
        if (
          isSsoSession() &&
          keycloak.authenticated &&
          retriableConfig &&
          !retriableConfig.kcRetried
        ) {
          try {
            await keycloak.updateToken(-1)
            // Re-read from localStorage — the single source of truth the
            // request interceptor also uses. onTokens may have DECLINED the
            // write (concurrent teardown cleared tokenSource); retrying with
            // keycloak.token directly would bypass that decision.
            const freshToken = localStorage.getItem('token')
            if (isSsoSession() && freshToken) {
              retriableConfig.kcRetried = true
              retriableConfig.headers.Authorization = `Bearer ${freshToken}`
              return axios.request(retriableConfig)
            }
          } catch {
            // Refresh failed — onAuthRefreshError tears the session down;
            // fall through to the invalid-token handling below.
          }
        }

        const errorData = error.response?.data as any
        if (isTokenInvalidResponse(errorData)) {
          // Token is bad: wipe the LOCAL session only. endSsoSession=false —
          // an automatic 401 must never escalate into a realm-wide Keycloak
          // single logout (routine expiry would log the user out of every
          // client in the realm, and on reload it can even kill a session
          // the silent check-sso just restored).
          authService.logout(false)
          store.dispatch.profile.setProfile({})
        }
        // Otherwise: leave the session intact, let the caller handle the 401
      }

      return Promise.reject(error)
    }
  )

  return axios
}

export async function get<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const axios = getAxiosInstance()
  const { data } = await axios.get<T>(url, config)
  return data
}

export async function post<T>(
  url: string,
  requestData?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  const axios = getAxiosInstance()
  try {
    const response = await axios.post<T>(url, requestData, config)
    console.log({ response })
    if (response && response.data) {
      return Promise.resolve(response.data)
    }
  } catch (err: any) {
    if (err.response && err.response.data) {
      return Promise.reject(err.response.data)
    }

    return Promise.reject(err)
  }

  return Promise.reject()
}

export async function put<T>(
  url: string,
  requestData?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  const axios = getAxiosInstance()
  const { data } = await axios.put<T>(url, requestData, config)
  return data
}

export async function deleteReq<T>(url: string, requestData?: any): Promise<T> {
  const axios = getAxiosInstance()
  const { data } = await axios.delete<T>(url, { data: requestData })
  return data
}
